import type { FleetMessageAudiences } from "@/models/repositories/Driver";
import type { FleetMessageRecipientMode } from "@/types/fleetMessage";

export type FleetMessageRecipientOption = { value: FleetMessageRecipientMode; label: string };

/** The audiences before the workspace and membership are known. */
export const EMPTY_AUDIENCES: FleetMessageAudiences = { all: [], mine: [] };

/**
 * The feed ids a recipient option addresses.
 *
 * `table` carries none of its own — the sender is on their way back to the driver table to build the set — so the
 * pane's Send stays disabled until they return with rows checked.
 */
export function recipientsForMode(
  mode: FleetMessageRecipientMode | null,
  audiences: FleetMessageAudiences,
  tableSelection: string[],
): string[] {
  switch (mode) {
    case "selection":
      return tableSelection;
    case "all":
      return audiences.all;
    case "mine":
      return audiences.mine;
    default:
      return [];
  }
}

/** Whether every channel of the audience is already selected. False for an empty audience. */
export function audienceFullySelected(selected: string[], audience: string[]): boolean {
  if (audience.length === 0) return false;
  const selectedIds = new Set(selected);
  return audience.every((id) => selectedIds.has(id));
}

/**
 * The selection after clicking an audience shortcut: drop the audience when fully selected, else add what is missing.
 *
 * The selection keeps its order and newly added ids follow it, so no chip the sender already picked moves. Ids outside
 * the audience are never touched, which is what lets a hand-picked channel survive unchecking the shortcut.
 */
export function toggleAudience(selected: string[], audience: string[]): string[] {
  if (audienceFullySelected(selected, audience)) {
    const audienceIds = new Set(audience);
    return selected.filter((id) => !audienceIds.has(id));
  }
  const selectedIds = new Set(selected);
  return [...selected, ...audience.filter((id) => !selectedIds.has(id))];
}

/**
 * The recipient option a freshly opened pane starts on.
 *
 * Rows checked in the driver table are the sender's own most recent statement of who they meant, so they win; with
 * nothing checked the pane starts on whatever option the sender left it on, and on none at all the first time.
 */
export function initialRecipientMode(
  tableSelection: string[],
  storedMode: FleetMessageRecipientMode | null,
): FleetMessageRecipientMode | null {
  if (tableSelection.length > 0) return "selection";
  return storedMode === "selection" ? null : storedMode;
}

/** Whether two feed-id lists name the same channels in the same order. */
export function sameRecipients(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * The recipient options the pane offers, in order.
 *
 * With rows already checked in the driver table, the Edit selection button is the way back to that table, so the
 * table option is left out rather than offering a second route to the same place. With nothing checked it leads,
 * because building a set on the table is the replacement for the group tree that used to sit here.
 */
export function recipientOptions(
  audiences: FleetMessageAudiences,
  tableSelection: string[],
): FleetMessageRecipientOption[] {
  const audienceOptions: FleetMessageRecipientOption[] = [
    { value: "all", label: `All Drivers (${audiences.all.length})` },
    { value: "mine", label: `My Drivers (${audiences.mine.length})` },
  ];
  if (tableSelection.length > 0) return audienceOptions;
  return [{ value: "table", label: "Choose drivers from the table" }, ...audienceOptions];
}
